import { useEffect, useRef, useState, useMemo } from 'react';
import { initGoogleMaps } from '../../services/googleMaps';
import useTripStore from '../../context/TripStore';

const BackgroundMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const previewMarkerRef = useRef(null);
  const [error, setError] = useState(null);
  const { trip, places, itinerary, settings, navigation, mobileUI } = useTripStore();
  const travelMode = settings?.travelMode || 'WALKING';
  const isNavigating = navigation?.isActive;
  const currentStopIndex = navigation?.currentStopIndex || 0;
  const selectedRouteIndex = navigation?.selectedRouteIndex || 0;
  const previewLocation = mobileUI?.previewLocation;

  // Memoize routeAlternatives to prevent useEffect dependency issues
  const routeAlternatives = useMemo(
    () => navigation?.routeAlternatives || [],
    [navigation?.routeAlternatives]
  );

  useEffect(() => {
    const loadMap = async () => {
      setError(null);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:12',message:'loadMap started',data:{hasMapRef:!!mapRef.current,hasMapInstance:!!mapInstanceRef.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      try {
        console.log('BackgroundMap: Initializing Google Maps...');
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:16',message:'Calling initGoogleMaps',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
        // #endregion
        await initGoogleMaps();
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:19',message:'initGoogleMaps completed',data:{hasGoogleMaps:!!window.google,hasMaps:!!window.google?.maps},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
        // #endregion
        console.log('BackgroundMap: Google Maps initialized successfully');
        
        if (!mapRef.current) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:24',message:'mapRef.current is null, returning',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
          // #endregion
          return;
        }

        // Determine center point - default to New York City
        let center = { lat: 40.7128, lng: -74.0060 }; // New York City coordinates
        let zoom = 12; // Good zoom level for city view
        
        if (trip.city?.coordinates) {
          center = trip.city.coordinates;
          zoom = 13;
        } else if (trip.startLocation?.coordinates) {
          center = trip.startLocation.coordinates;
          zoom = 13;
        } else if (places.length > 0 && places[0].coordinates) {
          center = places[0].coordinates;
          zoom = 13;
        } else if (itinerary.planA?.stops?.length > 0) {
          const firstStop = itinerary.planA.stops[0];
          const place = places.find(p => p.name === firstStop.name);
          if (place?.coordinates) {
            center = place.coordinates;
            zoom = 13;
          }
        }

        // Create or update map
        if (!mapInstanceRef.current) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:52',message:'Creating new map instance',data:{center,zoom,hasGoogleMaps:!!window.google,hasMaps:!!window.google?.maps},timestamp:Date.now(),sessionId:'debug-session',runId:'run6',hypothesisId:'V'})}).catch(()=>{});
          // #endregion
          try {
            const mapOptions = {
            zoom: zoom,
            center: center,
            disableDefaultUI: false, // Enable default UI controls
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: true,
            gestureHandling: 'greedy', // Enable zoom with mouse wheel and pinch
            // Show labels and landmarks - no custom styles to hide them
          };
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:75',message:'About to create Map instance',data:{hasGoogleMaps:!!window.google,hasMaps:!!window.google?.maps,hasMapConstructor:!!window.google?.maps?.Map},timestamp:Date.now(),sessionId:'debug-session',runId:'run4',hypothesisId:'S'})}).catch(()=>{});
          // #endregion
          
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
          
          // Expose map instance globally for other components (like search results)
          window.backgroundMapInstance = mapInstanceRef.current;
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:58',message:'Map instance created successfully',data:{hasMapInstance:!!mapInstanceRef.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
          // #endregion
          } catch (mapError) {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:61',message:'Map creation failed',data:{error:mapError.message,errorName:mapError.name,stack:mapError.stack},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
            // #endregion
            throw mapError;
          }
        } else {
          mapInstanceRef.current.setCenter(center);
          mapInstanceRef.current.setZoom(zoom);
        }

        // Initialize markers array if needed
        if (!window.backgroundMapMarkers) {
          window.backgroundMapMarkers = [];
        }
        if (!window.backgroundMapStartMarker) {
          window.backgroundMapStartMarker = null;
        }
        if (!window.backgroundMapEndMarker) {
          window.backgroundMapEndMarker = null;
        }

        // Clear existing markers and routes
        window.backgroundMapMarkers.forEach(marker => marker.setMap(null));
        window.backgroundMapMarkers = [];
        
        if (window.backgroundMapStartMarker) {
          window.backgroundMapStartMarker.setMap(null);
          window.backgroundMapStartMarker = null;
        }
        
        if (window.backgroundMapEndMarker) {
          window.backgroundMapEndMarker.setMap(null);
          window.backgroundMapEndMarker = null;
        }
        
        if (window.backgroundMapRoute) {
          window.backgroundMapRoute.setMap(null);
          window.backgroundMapRoute = null;
        }

        // Clear alternative routes
        if (window.backgroundMapAlternativeRoutes) {
          window.backgroundMapAlternativeRoutes.forEach(route => route.setMap(null));
          window.backgroundMapAlternativeRoutes = [];
        }

        // Add start location marker (green)
        if (trip.startLocation?.coordinates && mapInstanceRef.current) {
          window.backgroundMapStartMarker = new window.google.maps.Marker({
            position: trip.startLocation.coordinates,
            map: mapInstanceRef.current,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#34D399', // Green
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            },
            title: `Start: ${trip.startLocation.name || 'Starting Point'}`,
            zIndex: 1000, // Ensure it's on top
          });
        }

        // Add end location marker (red)
        if (trip.endLocation?.coordinates && mapInstanceRef.current) {
          window.backgroundMapEndMarker = new window.google.maps.Marker({
            position: trip.endLocation.coordinates,
            map: mapInstanceRef.current,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#EF4444', // Red
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            },
            title: `End: ${trip.endLocation.name || 'Ending Point'}`,
            zIndex: 1000, // Ensure it's on top
          });
        }

        // Show itinerary stops if available
        const activePlan = itinerary.activePlan === 'a' ? itinerary.planA : itinerary.planB;
        if (activePlan?.stops?.length > 0 && mapInstanceRef.current) {
          const bounds = new window.google.maps.LatLngBounds();
          
          activePlan.stops.forEach((stop, index) => {
            const place = places.find(p => p.name === stop.name || p.placeId === stop.placeId);
            if (place?.coordinates) {
              const isCurrentStop = isNavigating && index === currentStopIndex;
              const isCompleted = isNavigating && navigation.completedStops.includes(index);
              
              // Use different marker styling for current stop during navigation
              const marker = new window.google.maps.Marker({
                position: place.coordinates,
                map: mapInstanceRef.current,
                label: {
                  text: String(index + 1),
                  color: 'white',
                  fontSize: isCurrentStop ? '14px' : '12px',
                  fontWeight: isCurrentStop ? 'bold' : 'normal',
                },
                icon: isCurrentStop ? {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 12,
                  fillColor: '#3B82F6', // Blue for current stop
                  fillOpacity: 1,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 3,
                } : isCompleted ? {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: '#10B981', // Green for completed
                  fillOpacity: 0.7,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 2,
                } : undefined,
                title: stop.name,
                zIndex: isCurrentStop ? 2000 : isCompleted ? 1500 : 1000,
              });
              window.backgroundMapMarkers.push(marker);
              bounds.extend(place.coordinates);
            }
          });

          // Draw route - if navigating, show route to current stop; otherwise show full route
          if (activePlan.stops.length > 1 && window.google?.maps?.DirectionsService) {
            // Set route color based on travel mode
            const routeColors = {
              WALKING: '#10B981',    // Green
              TRANSIT: '#3B82F6',    // Blue
              DRIVING: '#EF4444',    // Red
              BICYCLING: '#F59E0B', // Amber
            };
            const routeColor = routeColors[travelMode] || '#10B981';
            
            const directionsService = new window.google.maps.DirectionsService();
            
            // DIRECTIONS RENDERER CONFIGURATION:
            // - suppressMarkers: true - We use custom markers (numbered, colored by status)
            // - preserveViewport: false - Allow map to auto-fit to show the route
            // - Transit mode: Use default rendering (automatically shows walking in gray, transit in line colors)
            // - Other modes: Use custom single-color polyline (simpler, cleaner for walking/driving/biking)
            const directionsRenderer = new window.google.maps.DirectionsRenderer({
              map: mapInstanceRef.current,
              suppressMarkers: true, // We're using our own custom markers
              preserveViewport: false, // Auto-fit map to show route
              // Custom styling only for non-transit modes
              // Transit uses default multi-color rendering (walking + transit segments)
              ...(travelMode !== 'TRANSIT' ? {
                polylineOptions: {
                  strokeColor: routeColor, // Single color for entire route
                  strokeWeight: 4,
                  strokeOpacity: 0.8,
                },
              } : {
                // Transit: Let DirectionsRenderer use default styling
                // This automatically renders:
                // - Walking segments: Gray polylines
                // - Transit segments: Colored by transit line (blue for subway, etc.)
                // - Proper paths along actual transit routes (not straight lines)
              }),
            });
            window.backgroundMapRoute = directionsRenderer;

            // Build route based on navigation state and travel mode
            // 
            // ROUTE CALCULATION STRATEGY:
            // - During navigation: Show route to current stop only (user is following step-by-step)
            // - Not navigating: Show full route through all stops (overview of entire itinerary)
            // - Transit mode: Calculate point-to-point (waypoints not well supported by transit API)
            // - Other modes: Can use waypoints to show route through all stops
            //
            let origin, destination, waypoints = [];
            
            if (isNavigating && currentStopIndex > 0) {
              // NAVIGATION MODE - Subsequent stops:
              // User is actively following the itinerary, so show route from previous stop to current stop.
              // This provides focused, step-by-step navigation guidance.
              const previousStop = activePlan.stops[currentStopIndex - 1];
              const previousPlace = places.find(p => p.name === previousStop.name || p.placeId === previousStop.placeId);
              const currentPlace = places.find(p => {
                const stop = activePlan.stops[currentStopIndex];
                return p.name === stop.name || p.placeId === stop.placeId;
              });
              
              if (previousPlace?.coordinates && currentPlace?.coordinates) {
                origin = previousPlace.coordinates;
                destination = currentPlace.coordinates;
              }
            } else if (isNavigating && currentStopIndex === 0) {
              // NAVIGATION MODE - First stop:
              // User just started navigation, show route from start location to first stop.
              // This is the initial navigation instruction.
              const firstStop = activePlan.stops[0];
              const firstPlace = places.find(p => p.name === firstStop.name || p.placeId === firstStop.placeId);
              
              origin = trip.startLocation?.coordinates;
              destination = firstPlace?.coordinates;
            } else {
              // OVERVIEW MODE - Not navigating:
              // User is viewing the full itinerary, so show complete route through all stops.
              // This gives a bird's-eye view of the entire trip route.
              const stopCoordinates = activePlan.stops.map(stop => {
                const place = places.find(p => p.name === stop.name || p.placeId === stop.placeId);
                return place?.coordinates || null;
              }).filter(Boolean);
              
              if (stopCoordinates.length > 0) {
                // Use start/end locations if available, otherwise use first/last stops
                origin = trip.startLocation?.coordinates || stopCoordinates[0];
                destination = trip.endLocation?.coordinates || stopCoordinates[stopCoordinates.length - 1];
                
                // Build waypoints: All stops except origin and destination
                // This allows the route to pass through all intermediate stops.
                // Note: For transit mode, waypoints will be removed later as transit API doesn't handle them well.
                stopCoordinates.forEach((coords, index) => {
                  const isOrigin = origin && Math.abs(coords.lat - origin.lat) < 0.0001 && Math.abs(coords.lng - origin.lng) < 0.0001;
                  const isDestination = destination && Math.abs(coords.lat - destination.lat) < 0.0001 && Math.abs(coords.lng - destination.lng) < 0.0001;
                  
                  if (!isOrigin && !isDestination) {
                    waypoints.push({ location: coords, stopover: true });
                  }
                });
              }
            }
            
            if (origin && destination) {
                // Build route request based on travel mode
                const routeRequest = {
                  origin: origin,
                  destination: destination,
                  // TRANSIT MODE LIMITATION:
                  // Google Maps Transit API doesn't reliably handle waypoints - it may show straight lines
                  // or fail to find routes. For transit, calculate point-to-point routes only.
                  // For other modes (walking, driving, biking), waypoints work well and show proper routes.
                  waypoints: travelMode === 'TRANSIT' ? [] : waypoints,
                  travelMode: window.google.maps.TravelMode[travelMode],
                  optimizeWaypoints: false, // Don't optimize - preserve the itinerary order
                };

                // Add mode-specific routing options
                if (travelMode === 'WALKING') {
                  // Walking: Avoid roads not suitable for pedestrians
                  routeRequest.avoidHighways = true;
                  routeRequest.avoidFerries = true;
                } else if (travelMode === 'DRIVING') {
                  // Driving: Allow highways and tolls (user can adjust preferences if needed)
                  routeRequest.avoidHighways = false;
                  routeRequest.avoidTolls = false;
                } else if (travelMode === 'BICYCLING') {
                  // Bicycling: Similar to walking - avoid highways and ferries
                  routeRequest.avoidHighways = true;
                  routeRequest.avoidFerries = true;
                } else if (travelMode === 'TRANSIT') {
                  // TRANSIT OPTIONS:
                  // - departureTime: Use current time for real-time transit schedules
                  // - modes: Include all transit types (bus, subway, train, etc.)
                  // - routingPreference: Prefer routes with less walking (more convenient)
                  // Note: DirectionsRenderer will automatically show walking segments (gray) 
                  // and transit segments (colored by line) when rendering transit routes.
                  routeRequest.transitOptions = {
                    departureTime: new Date(),
                    modes: ['BUS', 'SUBWAY', 'TRAIN', 'TRAM', 'RAIL'],
                    routingPreference: 'LESS_WALKING',
                  };
                }

                directionsService.route(routeRequest, (result, status) => {
                  if (status === 'OK' && directionsRenderer) {
                    // ROUTE RENDERING:
                    // - Transit: DirectionsRenderer uses default styling (gray for walking, colored for transit lines)
                    // - Other modes: Uses custom polylineOptions set above (single color for entire route)
                    // The DirectionsRenderer automatically handles complex transit routes with multiple
                    // walking segments and transit segments, showing them with appropriate colors.
                    directionsRenderer.setDirections(result);

                    // ROUTE ALTERNATIVES RENDERING:
                    // When we have alternatives from navigation state, render them
                    if (isNavigating && routeAlternatives.length > 1 && travelMode !== 'TRANSIT') {
                      window.backgroundMapAlternativeRoutes = [];

                      routeAlternatives.forEach((route, idx) => {
                        if (idx === selectedRouteIndex) return; // Skip selected route (already rendered above)

                        // Create a polyline for each alternative route
                        if (route.steps && route.steps.length > 0) {
                          // Decode polyline or use step end locations
                          const altPolyline = new window.google.maps.Polyline({
                            path: result.routes[idx]?.overview_path || [],
                            geodesic: true,
                            strokeColor: '#9CA3AF', // Gray for alternatives
                            strokeOpacity: 0.5,
                            strokeWeight: 3,
                            map: mapInstanceRef.current,
                            zIndex: 1,
                          });
                          window.backgroundMapAlternativeRoutes.push(altPolyline);
                        }
                      });
                    }
                  } else {
                    console.warn(`Route calculation failed for ${travelMode}:`, status);
                    // FALLBACK STRATEGY:
                    // - Transit: Don't show fallback (straight line would be misleading - transit routes are complex)
                    // - Other modes: Show simple polyline connecting points (better than nothing, but not ideal)
                    // This fallback is only for non-transit modes where a simple path is acceptable.
                    if (travelMode !== 'TRANSIT' && origin && destination) {
                      const path = waypoints.length > 0
                        ? [origin, ...waypoints.map(wp => wp.location), destination].filter(Boolean)
                        : [origin, destination].filter(Boolean);
                      const polyline = new window.google.maps.Polyline({
                        path: path,
                        geodesic: true,
                        strokeColor: routeColor,
                        strokeOpacity: 0.8,
                        strokeWeight: 4,
                        map: mapInstanceRef.current,
                      });
                      window.backgroundMapRoute = polyline;
                    }
                  }
                });
              }
            }

          // Fit bounds to show all markers
          if (window.backgroundMapMarkers.length > 0) {
            mapInstanceRef.current.fitBounds(bounds);
          }
        } 
        // Otherwise show places if we have them
        else if (places.length > 0 && mapInstanceRef.current) {
          places.forEach((place, index) => {
            if (place.coordinates) {
              const marker = new window.google.maps.Marker({
                position: place.coordinates,
                map: mapInstanceRef.current,
                label: {
                  text: String(index + 1),
                  color: 'white',
                  fontSize: '12px',
                },
                title: place.name,
              });
              window.backgroundMapMarkers.push(marker);
            }
          });

          // Fit bounds to show all markers
          if (window.backgroundMapMarkers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            window.backgroundMapMarkers.forEach(marker => {
              bounds.extend(marker.getPosition());
            });
            mapInstanceRef.current.fitBounds(bounds);
          }
        }
      } catch (error) {
        console.error('Error loading background map:', error);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'BackgroundMap.jsx:165',message:'loadMap catch block',data:{error:error.message,errorName:error.name,errorStack:error.stack},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H'})}).catch(()=>{});
        // #endregion
        setError(error.message || 'Failed to load Google Maps');
      }
    };

    loadMap();
    
    return () => {
      if (window.backgroundMapMarkers) {
        window.backgroundMapMarkers.forEach(marker => marker.setMap(null));
        window.backgroundMapMarkers = [];
      }
      if (window.backgroundMapStartMarker) {
        window.backgroundMapStartMarker.setMap(null);
        window.backgroundMapStartMarker = null;
      }
      if (window.backgroundMapEndMarker) {
        window.backgroundMapEndMarker.setMap(null);
        window.backgroundMapEndMarker = null;
      }
      if (window.backgroundMapRoute) {
        window.backgroundMapRoute.setMap(null);
        window.backgroundMapRoute = null;
      }
      if (window.backgroundMapAlternativeRoutes) {
        window.backgroundMapAlternativeRoutes.forEach(route => route.setMap(null));
        window.backgroundMapAlternativeRoutes = [];
      }
    };
    }, [trip.city, trip.startLocation, trip.endLocation, places, itinerary.planA, itinerary.planB, itinerary.activePlan, travelMode, isNavigating, currentStopIndex, navigation.completedStops, routeAlternatives, selectedRouteIndex]);

  // Handle preview marker for search results hover
  useEffect(() => {
    // Clean up existing preview marker
    if (previewMarkerRef.current) {
      previewMarkerRef.current.setMap(null);
      previewMarkerRef.current = null;
    }

    // Create new preview marker if we have a location and map is ready
    if (previewLocation?.coordinates && mapInstanceRef.current && window.google?.maps) {
      previewMarkerRef.current = new window.google.maps.Marker({
        position: previewLocation.coordinates,
        map: mapInstanceRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#3B82F6', // Blue
          fillOpacity: 0.9,
          strokeColor: '#FFFFFF',
          strokeWeight: 3,
        },
        title: previewLocation.name || 'Preview',
        zIndex: 3000, // Above all other markers
        animation: window.google.maps.Animation.DROP,
      });

      // Pan map to show the preview marker
      mapInstanceRef.current.panTo(previewLocation.coordinates);
    }

    return () => {
      if (previewMarkerRef.current) {
        previewMarkerRef.current.setMap(null);
        previewMarkerRef.current = null;
      }
    };
  }, [previewLocation]);

  return (
    <>
      <div 
        ref={mapRef} 
        className="fixed inset-0 w-full h-full z-0"
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          width: '100%',
          height: '100%',
          minHeight: '100vh'
        }}
      />
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-800 p-4 rounded z-50 max-w-md text-xs">
          <div className="font-semibold mb-1">Map Loading Error</div>
          <div>{error}</div>
          <div className="mt-2 text-red-600">
            Check browser console for details. Make sure:
            <ul className="list-disc list-inside mt-1 ml-2">
              <li>.env file exists in the app directory</li>
              <li>REACT_APP_GOOGLE_MAPS_API_KEY is set</li>
              <li>Dev server was restarted after creating/updating .env</li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default BackgroundMap;

